import { z } from 'zod';
import { TimestampSchema } from '../common.js';
import { AgentVoiceCaptureFieldsSchema } from '../entities/agentVoiceCapture.js';
import { AgentVoiceProfileFieldsSchema, VoiceConsentLanguageSchema, VoiceConsentMimeSchema, VoiceMobileRecordingMimeSchema, } from '../entities/agentVoiceProfile.js';
export const AgentVoiceProfileSummarySchema = AgentVoiceProfileFieldsSchema.pick({
    profileId: true,
    status: true,
    name: true,
    sampleSource: true,
    sourceMediaId: true,
    consentLanguage: true,
    failureCode: true,
    failureMessage: true,
    onboardingCompletedAt: true,
    createdAt: true,
    updatedAt: true,
    activatedAt: true,
});
export const AgentVoiceCaptureSummarySchema = AgentVoiceCaptureFieldsSchema.pick({
    captureId: true,
    status: true,
    name: true,
    consentLanguage: true,
    failureCode: true,
    failureMessage: true,
    createdAt: true,
    updatedAt: true,
    expiresAt: true,
}).extend({
    hasConsent: z.boolean(),
    hasSample: z.boolean(),
});
export const AgentVoiceCloneStatusResponseSchema = z.object({
    enabled: z.boolean(),
    profile: AgentVoiceProfileSummarySchema.nullable(),
    capture: AgentVoiceCaptureSummarySchema.nullable(),
    consentPhrases: z.record(VoiceConsentLanguageSchema, z.string().min(1)),
});
export const AgentVoiceCloneSetupRequestSchema = z.object({
    name: z.string().trim().min(1).max(64),
    sourceMediaId: z.string().uuid(),
    consentLanguage: VoiceConsentLanguageSchema,
    consentMime: VoiceConsentMimeSchema,
    consentBytes: z
        .number()
        .int()
        .positive()
        .max(10 * 1024 * 1024),
});
export const AgentVoiceCloneSetupResponseSchema = z.object({
    profile: AgentVoiceProfileSummarySchema,
    uploadUrl: z.string().url(),
    expiresAt: TimestampSchema,
});
export const AgentVoiceCloneActionResponseSchema = z.object({
    profile: AgentVoiceProfileSummarySchema,
});
export const AgentVoiceCaptureCreateRequestSchema = z.object({
    name: z.string().trim().min(1).max(64),
    consentLanguage: VoiceConsentLanguageSchema,
});
export const AgentVoiceCaptureCreateResponseSchema = z.object({
    capture: AgentVoiceCaptureSummarySchema,
    captureToken: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
});
export const AgentVoiceCaptureActionResponseSchema = z.object({
    capture: AgentVoiceCaptureSummarySchema,
});
export const PublicVoiceCaptureResponseSchema = z.object({
    captureId: z.string().uuid(),
    agentName: z.string().min(1).max(120),
    voiceName: z.string().min(1).max(64),
    consentLanguage: VoiceConsentLanguageSchema,
    consentPhrase: z.string().min(1),
    status: AgentVoiceCaptureFieldsSchema.shape.status,
    expiresAt: TimestampSchema,
    hasConsent: z.boolean(),
    hasSample: z.boolean(),
    failureMessage: z.string().min(1).max(500).optional(),
});
export const PublicVoiceCaptureUploadRequestSchema = z
    .object({
    kind: z.enum(['consent', 'sample']),
    mime: VoiceMobileRecordingMimeSchema,
    bytes: z
        .number()
        .int()
        .positive()
        .max(25 * 1024 * 1024),
    durationMs: z.number().int().min(1_000).max(30_000),
})
    .superRefine((recording, ctx) => {
    if (recording.kind === 'consent' && recording.bytes > 10 * 1024 * 1024) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['bytes'],
            message: 'Consent recordings cannot exceed 10 MB.',
        });
    }
    if (recording.kind === 'sample' && recording.durationMs < 5_000) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['durationMs'],
            message: 'Voice samples must be at least 5 seconds.',
        });
    }
});
export const PublicVoiceCaptureUploadResponseSchema = z.object({
    uploadUrl: z.string().url(),
    expiresAt: TimestampSchema,
});
export const PublicVoiceCaptureCompleteResponseSchema = z.object({
    status: z.literal('processing'),
});
//# sourceMappingURL=agentVoiceProfiles.js.map